import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { tokenStore } from './tokenStore';
import { authEvents } from './authEvents';
import type { LoginResponse, SessionContext } from './session.types';
import { authService } from '../api/services/authService';
import type { LoginRequest } from '../api/types/auth.types';

interface SessionState {
  session: SessionContext | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (req: LoginRequest) => Promise<SessionContext>;
  logout: () => Promise<void>;
  switchProperty: (entityId: string) => void;
}

export const SessionCtx = createContext<SessionState | null>(null);

function resolveSelectedProperty(session: SessionContext): string | null {
  const accessible = new Set(session.properties.map((p) => p.entityId));
  const stored = tokenStore.getSelectedPropertyId();
  if (stored && accessible.has(stored)) return stored;
  if (session.defaultPropertyId && accessible.has(session.defaultPropertyId)) {
    return session.defaultPropertyId;
  }
  return session.properties[0]?.entityId ?? null;
}

function applyLoginPayload(payload: LoginResponse): SessionContext {
  tokenStore.setAccessToken(payload.accessToken);
  // Only persist a refresh token when the BFF actually issued one — the
  // template BFF currently returns an empty string. Once real refresh tokens
  // land, this branch runs on every login.
  if (payload.refreshToken) {
    tokenStore.setRefreshToken(payload.refreshToken);
  }
  const selected = resolveSelectedProperty(payload.session);
  tokenStore.setSelectedPropertyId(selected);
  return { ...payload.session, selectedPropertyId: selected };
}

/**
 * Reads any session that was persisted to sessionStorage during a prior render
 * of this tab. Returns null if either the session JSON or the access token is
 * missing — both are required for API calls to succeed post-restore.
 */
function readInitialSession(): SessionContext | null {
  const persisted = tokenStore.getPersistedSession();
  const access = tokenStore.getAccessToken();
  if (!persisted || !access) return null;
  return persisted;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // Restore synchronously from sessionStorage so an F5 does not flash the
  // login/splash screen. If nothing is persisted, session starts as null.
  const [session, setSession] = useState<SessionContext | null>(readInitialSession);

  // isBootstrapping is only true when we have NO persisted session AND a
  // refresh token that might yield one. Without either, there's nothing to
  // wait for and we can show the login screen immediately.
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (readInitialSession() !== null) return false;
    return tokenStore.getRefreshToken() !== null;
  });

  // Keep the persisted copy in sync whenever the in-memory session changes.
  // Wrapped setter — use this instead of raw setSession everywhere below.
  const commitSession = useCallback((next: SessionContext | null) => {
    setSession(next);
    tokenStore.setPersistedSession(next);
  }, []);

  // Refresh-token bootstrap (falls back path). Only runs when nothing was
  // restored from sessionStorage and a refresh token is present.
  useEffect(() => {
    if (session !== null) return; // already restored
    if (!tokenStore.getRefreshToken()) return;

    let cancelled = false;
    (async () => {
      try {
        const ctx = await authService.getSessionContext();
        if (cancelled) return;
        const selected = resolveSelectedProperty(ctx);
        tokenStore.setSelectedPropertyId(selected);
        commitSession({ ...ctx, selectedPropertyId: selected });
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for the axios interceptor's 'cleared' event (refresh failed mid-flight).
  useEffect(() => {
    return authEvents.on('cleared', () => commitSession(null));
  }, [commitSession]);

  const login = useCallback(
    async (req: LoginRequest) => {
      const payload = await authService.login(req);
      const ctx = applyLoginPayload(payload);
      commitSession(ctx);
      return ctx;
    },
    [commitSession],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    tokenStore.clear();
    commitSession(null);
  }, [commitSession]);

  const switchProperty = useCallback(
    (entityId: string) => {
      setSession((prev) => {
        if (!prev) return prev;
        const next = { ...prev, selectedPropertyId: entityId };
        tokenStore.setPersistedSession(next);
        return next;
      });
      tokenStore.setSelectedPropertyId(entityId);
    },
    [],
  );

  const value = useMemo<SessionState>(
    () => ({
      session,
      isAuthenticated: session !== null,
      isBootstrapping,
      login,
      logout,
      switchProperty,
    }),
    [session, isBootstrapping, login, logout, switchProperty],
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}
